import { MLS } from '@aloft/toolkit-shared';
import { Flex, Icon, Input, Select, Typography } from '@aloft/toolkit-sui';

import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { selectMLSIDs } from '../../../redux/features/selectors/accountSelectors.js';
import { useAppSelector } from '../../../redux/hooks.js';
import { hasAtLeastOneActiveMLS } from '../../../utils/mls/hasAtLeastOneActiveMLS.js';
import { AddressInput } from '../../AddressInput/AddressInput.js';
import { AddressInputErrors } from '../../AddressInput/AddressInputErrors.js';
import { EditPropertyDetailsForm } from './types.js';

type EditPropertyDetailsModalProps = {
  form: UseFormReturn<z.input<EditPropertyDetailsForm>, unknown, z.infer<EditPropertyDetailsForm>>;
} & { addressLabel: string; showWarning: boolean; lockAddress: boolean };

export const EditPropertyDetailsModalContent = ({
  form,
  addressLabel,
  showWarning,
  lockAddress,
}: EditPropertyDetailsModalProps) => {
  const mlsIDs = useAppSelector(selectMLSIDs);
  const { control, resetField } = form;

  const userHasAtLeastOneActiveMLS = hasAtLeastOneActiveMLS(mlsIDs);

  const mlsOptions = MLS._keys
    .filter((key) => MLS[key].isActive && userHasAtLeastOneActiveMLS && mlsIDs.includes(key))
    .map((key) => ({ label: MLS[key].shortName, value: key }));

  return (
    <Flex width="100%" height="100%" gap={4} flexDirection="column">
      <FormProvider {...form}>
        <AddressInput addressLabel={addressLabel} lockAddress={lockAddress} />
        {showWarning && (
          <Flex alignItems="center" gap={1} marginBottom="1rem" marginLeft=".75rem">
            <Icon icon={'warning'} size="lg" />
            <Typography>Changing the address may affect your order.</Typography>
          </Flex>
        )}
      </FormProvider>
      <Flex gap={2}>
        <Controller
          control={control}
          key={'mls'}
          name={'mls'}
          render={({ field }) => {
            return (
              <Select
                options={mlsOptions}
                {...field}
                onChange={(val) => field.onChange(val ?? '')}
                label="MLS"
              />
            );
          }}
        />
        <Controller
          control={control}
          key={'mlsListingID'}
          name={'mlsListingID'}
          render={({ field }) => {
            return (
              <Input
                type="text"
                {...field}
                label="MLS Listing ID"
                onClear={() =>
                  resetField('mlsListingID', {
                    defaultValue: '',
                  })
                }
              />
            );
          }}
        />
      </Flex>
      <FormProvider {...form}>
        <AddressInputErrors />
      </FormProvider>
    </Flex>
  );
};
